import { useState } from 'react';
import { Select as AntSelect } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPaperclip, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Field, FieldLabel, FormField, FormModal, PrimaryBtn } from '../../components';
import { useCreateOwner, useUpdateOwner, useUploadOwnerFiles } from '../../queries/owners';
import { useProperties } from '../../queries/properties';
import { FILE_TYPES, monthValue } from './constants';
import { Chip, Chips, Dropzone, FileRow } from './styled';

const TEXT_FIELDS = [
  { key: 'name', label: 'Owner name', placeholder: 'Full name or entity' },
  { key: 'phone', label: 'Phone', placeholder: '(555) 555-0100', type: 'tel' },
  { key: 'email', label: 'Email', placeholder: 'name@example.com', type: 'email' },
  { key: 'address', label: 'Address', placeholder: 'Mailing address' },
  { key: 'website', label: 'Website', placeholder: 'Optional' }
];

const initialForm = (lead) => ({
  name: lead?.name || '',
  phone: lead?.phone || '',
  email: lead?.email || '',
  address: lead?.address || '',
  website: lead?.website || '',
  listingIds: [],
  started: monthValue(new Date()),
  files: []
});

export function FileDrop({ onAdd }) {
  const [type, setType] = useState(FILE_TYPES[0]);
  const [over, setOver] = useState(false);
  const add = (list) => onAdd(Array.from(list, (file) => ({ file, name: file.name, type })));

  return (
    <Dropzone
      data-over={over}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        add(e.dataTransfer.files);
      }}
    >
      <input
        type="file"
        multiple
        accept=".pdf,.doc,.docx,image/*"
        aria-label="Upload files"
        onChange={(e) => {
          add(e.target.files);
          e.target.value = '';
        }}
      />
      <FontAwesomeIcon icon={faPaperclip} />
      <span>Drop the signed agreement or photos here</span>
      <Chips role="radiogroup" aria-label="File type">
        {FILE_TYPES.map((t) => (
          <Chip key={t} type="button" role="radio" aria-checked={t === type} $active={t === type} onClick={() => setType(t)}>
            {t}
          </Chip>
        ))}
      </Chips>
    </Dropzone>
  );
}

function FilesField({ files, onChange }) {
  return (
    <FormField>
      <FieldLabel as="span">Files</FieldLabel>
      <FileDrop onAdd={(added) => onChange([...files, ...added])} />
      {files.map((f, i) => (
        <FileRow as="div" key={`${f.name}-${i}`}>
          <div>
            <strong>{f.name}</strong>
            <small>{f.type}</small>
          </div>
          <Chip type="button" style={{ minWidth: 24, minHeight: 24 }} aria-label={`Remove ${f.name}`} onClick={() => onChange(files.filter((_, j) => j !== i))}>
            <FontAwesomeIcon icon={faXmark} />
          </Chip>
        </FileRow>
      ))}
    </FormField>
  );
}

export default function OwnerFormModal({ lead, onClose }) {
  const [form, setForm] = useState(() => initialForm(lead));
  const { rows: listings, isLoading } = useProperties();
  const create = useCreateOwner();
  const convert = useUpdateOwner({ success: (data) => `${data.owner.name} added to owners` });
  const mutation = lead ? convert : create;
  const upload = useUploadOwnerFiles();
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));
  const valid = Boolean(form.name.trim());

  const submit = () => {
    const { started, files, ...values } = form;
    const payload = { ...values, name: form.name.trim(), startedAt: started ? `${started}-01` : null };
    mutation.mutate(lead ? { ...payload, _id: lead._id, status: 'active' } : payload, {
      onSuccess: (data) => {
        if (files.length) upload.mutate({ id: data.owner._id, files });
        onClose();
      }
    });
  };

  return (
    <FormModal
      title="Add owner"
      subtitle="New management relationship"
      valid={valid}
      loading={mutation.isPending}
      footer={null}
      onSubmit={submit}
      onClose={onClose}
    >
      {TEXT_FIELDS.map((f) => (
        <FormField key={f.key}>
          <FieldLabel htmlFor={`owner-${f.key}`}>{f.label}</FieldLabel>
          <Field
            id={`owner-${f.key}`}
            type={f.type || 'text'}
            value={form[f.key]}
            onChange={set(f.key)}
            placeholder={f.placeholder}
            required={f.key === 'name'}
            autoFocus={f.key === 'name'}
          />
        </FormField>
      ))}
      <FormField>
        <FieldLabel htmlFor="owner-listings">Properties owned</FieldLabel>
        <AntSelect
          id="owner-listings"
          mode="multiple"
          size="large"
          value={form.listingIds}
          onChange={set('listingIds')}
          loading={isLoading}
          placeholder="Select units"
          optionFilterProp="label"
          options={listings.map((l) => ({ value: l._id, label: l.ownerName ? `${l.name} · ${l.ownerName}` : l.name }))}
        />
      </FormField>
      <FormField>
        <FieldLabel htmlFor="owner-started">Date started</FieldLabel>
        <Field id="owner-started" type="month" value={form.started} onChange={set('started')} placeholder="e.g. Sep 2026" />
      </FormField>
      <FilesField files={form.files} onChange={set('files')} />
      <PrimaryBtn type="submit" disabled={!valid || mutation.isPending} style={{ justifyContent: 'center', width: '100%' }}>
        <FontAwesomeIcon icon={faCheck} />
        {valid ? 'Save owner' : 'Enter an owner name'}
      </PrimaryBtn>
    </FormModal>
  );
}
